import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight,
  Cog,
  Smartphone,
  CheckCircle,
  Workflow,
  Zap
} from 'lucide-react';

// WhatsApp Icon SVG Component
const WhatsAppIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Navigation Component - Neo-Brutalist Clean
const Navigation = () => (
  <nav className="sticky top-0 z-50 bg-cream border-b-4 border-text-dark" data-testid="boostops-navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-4">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <span className="font-poppins font-black text-2xl md:text-3xl tracking-tight text-text-dark">
            BOOST<span className="text-pink">CHANGE</span>
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/" className="hidden md:flex items-center gap-2 font-poppins font-semibold text-text-dark hover:text-pink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <a
            href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20me%20interesa%20conocer%20c%C3%B3mo%20pueden%20optimizar%20y%20automatizar%20la%20tecnolog%C3%ADa%20en%20mi%20organizaci%C3%B3n."
            target="_blank"
            rel="noopener noreferrer"
            className="bg-lime text-text-dark font-poppins font-bold uppercase tracking-widest px-6 py-3 border-4 border-text-dark hover:translate-y-1 transition-all"
            style={{ boxShadow: '5px 5px 0px #000', borderRadius: '0px' }}
            data-testid="boostops-cta"
          >
            Optimiza ahora
          </a>
        </div>
      </div>
    </div>
  </nav>
);

// Hero Section - Cream background, "ágil" in lime
const HeroSection = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="boostops-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="space-y-8">
          <div 
            className="inline-block bg-white px-5 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Boost Ops</span>
          </div>
          
          <h1 className="font-poppins font-black text-4xl md:text-5xl lg:text-6xl text-text-dark leading-[1.05]" data-testid="boostops-title">
            Tecnología <span className="text-lime">ágil</span> que trabaja para ti
          </h1>
          
          <p className="font-poppins text-lg md:text-xl text-text-dark/80 leading-relaxed">
            Implementamos Soluciones Mínimas Efectivas. Evitamos la sobreingeniería y los cuellos de botella operativos; desarrollamos ecosistemas tecnológicos escalables que liberan a tu equipo para enfocarse en el impacto real.
          </p>

          <div className="flex flex-wrap gap-4 pt-4">
            <a
              href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20me%20interesa%20conocer%20c%C3%B3mo%20pueden%20optimizar%20y%20automatizar%20la%20tecnolog%C3%ADa%20en%20mi%20organizaci%C3%B3n."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-blue text-white font-poppins font-bold uppercase tracking-widest px-8 py-4 border-4 border-text-dark hover:translate-y-1 transition-all"
              style={{ 
                boxShadow: '6px 6px 0px #000',
                borderRadius: '0px'
              }}
              data-testid="boostops-hero-cta"
            >
              <WhatsAppIcon className="w-5 h-5" />
              DESCUBRE CÓMO
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Visual - Clean rectangular frame */}
        <div className="relative">
          <div 
            className="w-full overflow-hidden bg-white"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px #000',
              borderRadius: '0px'
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/pkkc1965_WhatsApp%20Image%202026-03-08%20at%2011.41.06%20PM.jpeg"
              alt="Comunidad Boost Ops"
              className="w-full h-auto"
              style={{ display: 'block', borderRadius: '0px' }}
            />
          </div>
          
          {/* Floating elements - No rotation */}
          <div 
            className="absolute -top-4 -right-4 bg-cyan p-3"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <Cog className="w-8 h-8 text-text-dark" />
          </div>
          
          <div 
            className="absolute -bottom-4 -left-4 bg-yellow px-4 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Sin cajas negras!</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section - 2 White Brutalist Cards
const ServicesSection = () => {
  const services = [
    {
      icon: <Workflow className="w-8 h-8" strokeWidth={2.5} />,
      title: "Automatización de Flujos Operativos",
      iconBg: "bg-cyan",
      description: "Liberamos a tu equipo del trabajo mecánico y repetitivo. Diseñamos ecosistemas digitales inteligentes que conectan tus herramientas actuales, poniendo a las personas en el centro. Menos horas navegando en plataformas dispersas, más horas enfocadas en la estrategia.",
      bullets: [
        "Ecosistemas digitales integrados",
        "Diseño y orquestación de flujos",
        "Conexión e interoperabilidad entre plataformas",
        "Sistemas de reportería 100% automatizada"
      ]
    },
    {
      icon: <Smartphone className="w-8 h-8" strokeWidth={2.5} />,
      title: "Soluciones Digitales de Alto Impacto",
      iconBg: "bg-pink",
      description: "Democratizamos el acceso a la tecnología. Transversalizamos la innovación en tu gestión con desarrollos ágiles (Soluciones Mínimas Efectivas) que agregan valor inmediato sin sacrificar tu presupuesto operativo.",
      bullets: [
        "Plataformas web interactivas y gamificadas",
        "Landing pages para levantamiento de fondos",
        "Digitalización de reportes y manuales",
        "Desarrollos sin código innecesario"
      ]
    },
  ];

  return (
    <section className="bg-cream py-16 md:py-24" data-testid="boostops-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-4">
            NUESTROS SERVICIOS
          </h2>
          <p className="font-poppins font-medium text-lg text-text-dark/70">Tecnología práctica para organizaciones reales</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white p-8"
              style={{ 
                border: '4px solid #1a1a1a',
                boxShadow: '8px 8px 0px #000',
                borderRadius: '0px'
              }}
              data-testid={`boostops-service-${index}`}
            >
              {/* Icon container */}
              <div 
                className={`${service.iconBg} p-4 inline-block mb-5`}
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <div className="text-text-dark">{service.icon}</div>
              </div>
              
              <h3 className="font-poppins font-bold text-xl md:text-2xl text-text-dark mb-4">{service.title}</h3>
              <p className="font-poppins text-text-dark/70 mb-6 leading-relaxed">{service.description}</p>
              
              {/* Clean checkmark bullets */}
              <ul className="space-y-2">
                {service.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
                    <span className="font-poppins text-sm text-text-dark">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Studies Section - 2 White Brutalist Cards (same style as Sistemas de Impacto)
const CaseStudiesSection = () => {
  const cases = [
    {
      badge: "CASO DE ÉXITO",
      title: "Lab XXI: Del PDF a la Plataforma Interactiva",
      description: "Transformamos un denso manual de buenas prácticas de 60 páginas en una herramienta web interactiva. Ahora, los profesionales sociales evalúan el nivel de inclusión de sus proyectos mediante un test dinámico y reciben recomendaciones accionables en tiempo real.",
      resultText: "Ver plataforma interactiva",
      resultUrl: "https://tool-atv-proyecto.vercel.app/",
      badgeColor: "bg-cyan"
    },
    {
      badge: "CASO DE ÉXITO",
      title: "Proyecto Fibras",
      description: "Desarrollamos la identidad y la plataforma digital para visibilizar este modelo de empoderamiento comunitario. Creamos una solución web ágil y de alto impacto diseñada específicamente para atraer partners estratégicos y facilitar el levantamiento de fondos con cooperantes.",
      resultText: "Ver Landing Page",
      resultUrl: "https://boost-change.com/proyecto-fibras",
      badgeColor: "bg-pink"
    },
  ];

  return (
    <section className="bg-cream py-16 md:py-24" data-testid="boostops-cases">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-4">
            CASOS DESTACADOS
          </h2>
          <p className="font-poppins font-medium text-lg text-text-dark/70">Transformaciones tecnológicas reales</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {cases.map((caseStudy, index) => (
            <div
              key={index}
              className="bg-white p-8"
              style={{ 
                border: '4px solid #1a1a1a',
                boxShadow: '8px 8px 0px #000',
                borderRadius: '0px'
              }}
              data-testid={`case-study-${index}`}
            >
              {/* Badge */}
              <div 
                className={`${caseStudy.badgeColor} px-4 py-2 inline-block mb-5`}
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <span className="font-poppins font-bold text-xs uppercase text-text-dark tracking-wide">{caseStudy.badge}</span>
              </div>
              
              <h3 className="font-poppins font-black text-xl md:text-2xl text-text-dark mb-4">{caseStudy.title}</h3>
              
              <p className="font-poppins text-text-dark/80 mb-6 leading-relaxed">{caseStudy.description}</p>
              
              {/* Result button - Lima background, clickable */}
              <a
                href={caseStudy.resultUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-lime px-5 py-3 inline-flex items-center gap-2 hover:translate-y-1 transition-all cursor-pointer"
                style={{ 
                  border: '3px solid #1a1a1a',
                  boxShadow: '4px 4px 0px #000',
                  borderRadius: '0px'
                }}
              >
                <span className="font-poppins font-bold text-sm text-text-dark">{caseStudy.resultText}</span>
                <ArrowRight className="w-4 h-4 text-text-dark" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section - Consolidated closing with cream background
const CTASection = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="boostops-cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div 
        className="bg-white p-10 md:p-12 text-center"
        style={{ 
          border: '4px solid #1a1a1a',
          boxShadow: '8px 8px 0px #000',
          borderRadius: '0px'
        }}
      >
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark mb-6 leading-tight">
          No entregamos cajas negras.<br />
          Transferimos <span className="text-blue">capacidad</span>.
        </h2>
        <p className="font-poppins text-lg text-text-dark/70 mb-8 max-w-2xl mx-auto">
          Entrenamos a tu equipo para que entiendan, gestionen y escalen las soluciones tecnológicas por sí mismos. Tu autonomía es nuestro principal entregable.
        </p>
        <a
          href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20estoy%20listo%20para%20optimizar%20nuestra%20tecnolog%C3%ADa.%20%C2%BFPodemos%20agendar%20una%20revisi%C3%B3n%20de%20nuestros%20flujos%3F"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-lime text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-4 hover:translate-y-1 transition-all"
          style={{ 
            border: '4px solid #1a1a1a',
            boxShadow: '6px 6px 0px #000',
            borderRadius: '0px'
          }}
          data-testid="boostops-final-cta"
        >
          <Zap className="w-5 h-5" />
          OPTIMIZAR MI OPERACIÓN
          <ArrowRight className="w-5 h-5" />
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-text-dark py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <Link to="/" className="font-poppins font-black text-2xl text-white">
          BOOST<span className="text-pink">CHANGE</span>
        </Link>
        <div className="flex gap-6">
          <Link to="/" className="font-poppins text-white/80 hover:text-pink transition-colors">Inicio</Link>
          <Link to="/impact-boost-academy" className="font-poppins text-white/80 hover:text-pink transition-colors">Academy</Link>
          <Link to="/sistemas-de-impacto" className="font-poppins text-white/80 hover:text-pink transition-colors">Sistemas</Link>
        </div>
        <p className="font-poppins text-white/60 text-sm">2024 Boost Change</p>
      </div>
    </div>
  </footer>
);

// Main Page Component - Simplified structure
const BoostOpsPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="boostops-page">
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <CaseStudiesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default BoostOpsPage;
